import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, Box, Card, Menu, MenuItem, Typography } from '@mui/material';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { getHospitalDoctorsList } from '../../rtk-apis/reports/getHospitalDoctorsList';
import { getStudyMarkingsApi } from '../../rtk-apis/reports/getStudyMarkingsApi';
import AssignDoctorModal from '../patients/components/assignToModal';
import { STATUS_LABELS as DIST_STATUS_LABELS } from '../express/DistributionPage';
import ReportLabelsCell from './components/ReportLabelsCell';
import ReportLabelFilter from './components/ReportLabelFilter';
import MeasurementFilter from './components/MeasurementFilter';
import { MEASUREMENT_FILTER_ROLES } from './measurementFilterFields';
import {
  createReportLabel,
  deleteReportLabel,
  getReportLabels,
  setReportLabels,
  updateReportLabel,
  type ReportLabel,
} from '../../rtk-apis/reports/reportLabels';
import { fmtDate, fmtTime } from '../studies/CardiacStudyListV2';

/**
 * The date this list shows, matching the studies list.
 *
 * `studyDate` is the DICOM StudyDate — the same field the studies list prints, and what a
 * clinician means by the date of the scan. `date` is the report's own creation date and is
 * only a fallback, for reports stored before the list endpoint returned studyDate.
 */
const reportDate = (item: any) => fmtDate(item?.studyDate) || fmtDate(item?.date);
/** Time to go with `reportDate` — only meaningful alongside a DICOM studyDate, not the fallback. */
const reportTime = (item: any) => (item?.studyDate ? fmtTime(item?.studyTime) : '');
import '../studies/cardiacStudyList.css';
import './cardiacReportList.css';

/* ════════════════════════════════════════════════════════════════════════
   Cardiac Report List — bespoke report worklist. Mirrors the studies card UI.
   Presentational: consumes ReportList's data + the existing `actions` config
   (so all status/permission logic stays in ReportList). Theme: navy + teal.
   ════════════════════════════════════════════════════════════════════════ */

interface ActionItem {
  id: string;
  label: string;
  isVisible?: (item: any) => boolean;
  onClick: (item: any) => void;
}
interface Props {
  reports: any[];
  actions: { dropdownItems: ActionItem[] }[];
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  currentFilters: any;
  onFilterChange: (f: any) => void;
  onClearFilter: () => void;
  pagination: { currentPage: number; totalPages: number; pageSize: number; totalRecords: number };
  onPageChange: (n: number) => void;
  onItemsPerPageChange: (n: number) => void;
  loading?: boolean;
  role?: string | null;
  /** Called after a report is (re)assigned so the orchestrator refetches the list. */
  onAssigned?: () => void;
}

const statusClass = (s?: string) => {
  const v = (s || '').toLowerCase();
  if (v === 'finalized') {
    return 'csl-status--final';
  }
  if (v === 'preliminary') {
    return 'csl-status--prelim';
  }
  if (v === 'draft') {
    return 'csl-status--draft';
  }
  return 'csl-status--none';
};
/* ── row + header geometry ──────────────────────────────────────────────────
   ONE source of truth for the column layout: the header band and every row read the
   same constant, so a column can no longer drift away from its label. This is the
   pattern already proven on CardiacStudyListV2.

   Why this lives here in MUI rather than in cardiacReportList.css: the rows used to be
   styled through .csl-card / .csl-card__col, which are SHARED with the studies
   stylesheet. A breakpoint written for the studies list silently restyled report rows —
   most recently hiding Date and Type below 1200px via .csl-card__col:nth-child(4)/(5),
   which knocked every value out from under its heading. Owning the layout locally means
   studies CSS can no longer reach in here.

   Every track carries a PIXEL floor. A bare fr collapses to min-content when the
   viewport is tight, which is what crushed assigned names to "Veera Prasa…". With a
   floor the grid stops shrinking and the table scrolls sideways instead. */
const ACTIONS_W = '76px';
const GRID_COLS =
  `minmax(240px, 2.1fr) minmax(210px, 1.3fr) minmax(100px, 1.3fr) minmax(90px, 0.9fr) ` +
  `minmax(130px, 1fr) minmax(116px, 1fr) ${ACTIONS_W}`;
/* Receptionists get an extra Labels track between Distribution and Status. Labels takes
   the widest flexible share: two chips plus the add button is the normal case. */
const GRID_COLS_LABELS =
  `minmax(220px, 1.55fr) minmax(210px, 1fr) minmax(100px, 0.85fr) minmax(90px, 0.7fr) ` +
  `minmax(130px, 0.85fr) minmax(200px, 1.75fr) minmax(116px, 0.85fr) ${ACTIONS_W}`;
/* Sum of the floors + gaps + row padding. Header and rows MUST share this or the labels
   drift off their columns the moment the scroller moves. */
const ROW_MIN_W = 1100;
const ROW_MIN_W_LABELS = 1270;

const statusColor = (s?: string) => {
  const v = (s || '').toLowerCase();
  if (v === 'finalized') {
    return T.green;
  }
  if (v === 'preliminary') {
    return T.sky;
  }
  if (v === 'draft') {
    return T.amber;
  }
  return T.ink3;
};

const colSx = { minWidth: 0, display: 'flex', flexDirection: 'column' as const, gap: '2px' };
const vSx = {
  fontSize: 13.5,
  color: T.ink,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;
const headCellSx = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: T.ink3,
  whiteSpace: 'nowrap' as const,
  display: 'flex',
  alignItems: 'center',
};
const HeadCell: React.FC<{ children?: React.ReactNode; align?: 'start' | 'end' }> = ({
  children,
  align,
}) => (
  <Typography sx={{ ...headCellSx, justifyContent: align === 'end' ? 'flex-end' : 'flex-start' }}>
    {children}
  </Typography>
);

const initialsOf = (name?: string) => {
  if (!name) {
    return '—';
  }
  const parts = name.replace(/\^/g, ' ').replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '—';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const RPP = [10, 15, 25, 50];

const CardiacReportList: React.FC<Props> = ({
  reports,
  actions,
  searchTerm,
  setSearchTerm,
  currentFilters,
  onFilterChange,
  onClearFilter,
  pagination,
  onPageChange,
  onItemsPerPageChange,
  loading,
  role,
  onAssigned,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Assign / reassign a report to a doctor — same flow the old CustomTable carried.
  // Visible to the roles that manage assignment (Admin / Sonographer / Medical Receptionist).
  const canAssign = role === 'Admin' || role === 'Sonographer' || role === 'Medical Receptionist';
  // ---- Report labels (receptionist workflow markers) ----
  // Receptionists file finalized reports into Best Practice by hand and had no way to see
  // which were already done. Labels are theirs to manage; nobody else needs the column.
  const canUseLabels = role === 'Medical Receptionist';
  // Filter by measurement values (EF < 50 …) — the backend allows the same roles.
  const canFilterMeasurements = MEASUREMENT_FILTER_ROLES.includes(role || '');
  // The Labels column adds a grid track, so the row/header templates need the modifier.
  const rootClass = canUseLabels ? 'csl crl crl--labels' : 'csl crl';
  // Study UIDs marked as atrial fibrillation or critical, for the badges on each row.
  // One fetch per mount — the marks live in a single per-clinic document.
  const [afStudyUIDs, setAfStudyUIDs] = useState<string[]>([]);
  const [criticalStudyUIDs, setCriticalStudyUIDs] = useState<string[]>([]);
  useEffect(() => {
    dispatch(getStudyMarkingsApi())
      .unwrap()
      .then((res: any) => {
        setAfStudyUIDs(res?.data?.afList || []);
        setCriticalStudyUIDs(
          (res?.data?.alertList || []).map((x: any) => (typeof x === 'string' ? x : x?.studyInstanceUID))
        );
      })
      .catch(() => {
        setAfStudyUIDs([]);
        setCriticalStudyUIDs([]);
      });
  }, [dispatch]);
  const isCritical = (item: any) =>
    !!item.studyInstanceUID && criticalStudyUIDs.includes(item.studyInstanceUID);
  const [labels, setLabels] = useState<ReportLabel[]>([]);
  const [reportLabelMap, setReportLabelMap] = useState<Record<string, string[]>>({});
  const [labelBusyId, setLabelBusyId] = useState<string | null>(null);

  const refreshLabels = useCallback(async () => {
    try {
      const res = (await dispatch(getReportLabels()).unwrap()) as { result?: ReportLabel[] };
      setLabels(Array.isArray(res?.result) ? res.result : []);
    } catch {
      /* the column degrades to read-only rather than breaking the list */
    }
  }, [dispatch]);

  useEffect(() => {
    if (canUseLabels) {
      refreshLabels();
    }
  }, [canUseLabels, refreshLabels]);

  // Seed from the server rows, but never clobber an edit already made in this session.
  useEffect(() => {
    setReportLabelMap(prev => {
      const next = { ...prev };
      reports.forEach((r: { _id: string; labels?: string[] }) => {
        if (next[r._id] === undefined) {
          next[r._id] = Array.isArray(r.labels) ? r.labels : [];
        }
      });
      return next;
    });
  }, [reports]);

  const handleLabelsChange = async (reportId: string, labelIds: string[]) => {
    const previous = reportLabelMap[reportId] || [];
    setReportLabelMap(prev => ({ ...prev, [reportId]: labelIds })); // optimistic
    setLabelBusyId(reportId);
    try {
      await dispatch(setReportLabels({ reportId, labels: labelIds })).unwrap();
    } catch {
      setReportLabelMap(prev => ({ ...prev, [reportId]: previous })); // roll back on failure
    } finally {
      setLabelBusyId(null);
    }
  };

  const handleCreateLabel = async (name: string): Promise<ReportLabel | null> => {
    try {
      const res = (await dispatch(createReportLabel({ name })).unwrap()) as {
        result?: ReportLabel;
      };
      const created = res?.result || null;
      if (created) {
        setLabels(prev => (prev.some(l => l._id === created._id) ? prev : [...prev, created]));
      }
      return created;
    } catch {
      return null;
    }
  };

  const handleRenameLabel = async (labelId: string, name: string) => {
    setLabels(prev => prev.map(l => (l._id === labelId ? { ...l, name } : l)));
    try {
      await dispatch(updateReportLabel({ labelId, name })).unwrap();
    } catch {
      refreshLabels(); // name clash or failure — take the server's word
    }
  };

  const handleDeleteLabel = async (labelId: string) => {
    setLabels(prev => prev.filter(l => l._id !== labelId));
    setReportLabelMap(prev => {
      const next: Record<string, string[]> = {};
      Object.entries(prev).forEach(([id, ids]) => {
        next[id] = ids.filter(x => x !== labelId);
      });
      return next;
    });
    try {
      await dispatch(deleteReportLabel({ labelId })).unwrap();
    } catch {
      refreshLabels();
    }
  };

  // MUI renders the picker dialog and its menus in a portal, but React still bubbles their
  // events up the COMPONENT tree — so a backdrop click was reaching the row's onClick and
  // navigating to the report. Stopping here covers every portal the cell opens.
  const renderLabels = (item: { _id: string; name?: string }) => (
    <span
      onClick={e => e.stopPropagation()}
      role="presentation"
    >
      <ReportLabelsCell
        reportId={item._id}
        value={reportLabelMap[item._id] || []}
        labels={labels}
        patientName={item.name || ''}
        busy={labelBusyId === item._id}
        onChange={handleLabelsChange}
        onCreateLabel={handleCreateLabel}
        onRenameLabel={handleRenameLabel}
        onDeleteLabel={handleDeleteLabel}
      />
    </span>
  );

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignReportId, setAssignReportId] = useState('');
  const [assignStatus, setAssignStatus] = useState<'Assign' | 'Reassign'>('Assign');
  const handleAssignClick = (reportId: string, status: 'Assign' | 'Reassign') => {
    setAssignReportId(reportId);
    setAssignStatus(status);
    setAssignModalOpen(true);
  };
  // Renders the "Assigned" cell: + Assign when unassigned, name + Update when assigned
  // (Update hidden once Finalized); read-only name for roles that can't assign.
  const renderAssigned = (item: any) => {
    const name = item.reportAssignedTo || item.assignTo;
    const isAssigned = !!item.reportAssignedTo;
    if (canAssign) {
      if (!isAssigned) {
        return (
          <button
            type="button"
            className="crl-assign-btn"
            onClick={e => {
              e.stopPropagation();
              handleAssignClick(item._id, 'Assign');
            }}
          >
            + Assign
          </button>
        );
      }
      return (
        <span className="crl-assigned">
          <span
            className="crl-assigned__name"
            title={item.reportAssignedToEmail || ''}
          >
            {name}
          </span>
          {item.status !== 'Finalized' && (
            <button
              type="button"
              className="crl-reassign-btn"
              onClick={e => {
                e.stopPropagation();
                handleAssignClick(item._id, 'Reassign');
              }}
            >
              Update
            </button>
          )}
        </span>
      );
    }
    return <>{name || '—'}</>;
  };
  // Card click — mirrors the original CustomTable.handleNameClick (from='reports'):
  // Receptionist / Practice Manager have no Express access → View Report;
  // everyone else opens the Express editor for the study.
  const openReport = (item: any) => {
    if (!item) {
      return;
    }
    if (role === 'Medical Receptionist' || role === 'Practice Manager') {
      navigate(`/patientReports/${item._id}`, { state: { reportId: item._id } });
      return;
    }
    navigate(`/express/${item.studyInstanceUID}`, {
      state: {
        reportId: item._id,
        patientName: item.name,
        modalities: item.modality,
        mrn: item.patientId,
        date: item.date,
        time: item.time,
        instances: item.numberOfImages,
        studyInstanceUID: item.studyInstanceUID,
        ...item,
      },
    });
  };
  const [moreOpen, setMoreOpen] = useState(false);
  // Row-actions dropdown as a portaled MUI Menu: it escapes the .csl-list overflow
  // scroller (so it can never be clipped), auto-repositions to stay in the viewport,
  // and closes on outside-click / Escape / scroll — replacing the old absolutely-
  // positioned div + manual open-upward hack that got clipped at the container edge.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<any>(null);
  const openMenu = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuTarget?._id === item._id) {
      closeMenu();
      return;
    }
    setMenuTarget(item);
    setMenuAnchor(e.currentTarget as HTMLElement);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };
  // Human-readable per-recipient distribution detail for the hover tooltip (matches prod).
  const distTitle = (d: any): string | undefined => {
    const list = d?.distributions;
    if (!Array.isArray(list) || !list.length) {
      return undefined;
    }
    return list
      .map(
        (x: any, i: number) =>
          `${i + 1}. ${x.toProviderName || 'Recipient'} — ${DIST_STATUS_LABELS[x.status] || x.status || '—'}` +
          `${x.createdAt ? ` — ${new Date(x.createdAt).toLocaleDateString()}` : ''}`
      )
      .join('\n');
  };
  // Rich label + click-through to the distribution page — parity with the old
  // CustomTable's receptionist distribution column, now for every role.
  const distStatusText = (d: any) =>
    d.count === 1
      ? DIST_STATUS_LABELS[d.distributions?.[0]?.status] || d.distributions?.[0]?.status || 'Sent'
      : 'distributions';
  const openDistribution = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.studyInstanceUID) {
      return;
    }
    navigate(`/distribution/${item.studyInstanceUID}`, {
      state: {
        reportId: item._id,
        studyData: {
          name: item.name,
          patientName: item.name,
          dob: item.dob,
          sex: item.sex,
          age: item.age,
          patientId: item.patientId,
          reportName: item.reportName,
          studyInstanceUID: item.studyInstanceUID,
        },
      },
    });
  };
  const renderDist = (item: any) => {
    const dist = item._distData;
    return dist && dist.count > 0 ? (
      <button
        type="button"
        className="crl-dist-link"
        title={distTitle(dist)}
        onClick={e => openDistribution(item, e)}
      >
        <span className="crl-dist-link__count">{dist.count}</span>
        <span className="crl-dist-link__label">{distStatusText(dist)}</span>
      </button>
    ) : (
      <span className="csl-sub">None</span>
    );
  };
  // AF badge — mirrors the study list. The mark is held per clinic against the
  // study UID, so a report inherits it from its study without storing anything
  // of its own. Amber rather than red: AF changes how the reader interprets the
  // numbers, it is not an urgent finding.
  const renderAF = (item: any) =>
    item.studyInstanceUID && afStudyUIDs.includes(item.studyInstanceUID) ? (
      <Box
        component="span"
        title="Atrial fibrillation — EF is averaged across beats"
        sx={{
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.06em',
          lineHeight: 1,
          padding: '3px 5px',
          borderRadius: '4px',
          color: T.amber,
          background: 'rgba(247,163,90,0.14)',
          border: '1px solid rgba(247,163,90,0.45)',
        }}
      >
        AF
      </Box>
    ) : null;
  // Critical badge — the study list's flag, read from the same per-clinic marks.
  const renderCritical = (item: any) =>
    isCritical(item) ? (
      <Box
        component="span"
        title="Critical"
        sx={{
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.06em',
          lineHeight: 1,
          padding: '3px 5px',
          marginRight: '4px',
          borderRadius: '4px',
          color: T.red,
          background: 'rgba(251,113,133,0.14)',
          border: '1px solid rgba(251,113,133,0.5)',
        }}
      >
        CRITICAL
      </Box>
    ) : null;
  // Pending-review reminder bell — parity with the old table's name-cell bell.
  const renderBell = (item: any) =>
    item.pendingReview ? (
      <span
        className="crl-bell"
        title={`Reminded — ${item.reminderCount || 1} time(s)`}
      >
        🔔
      </span>
    ) : null;
  // List (rows) view is the default for every role; tiles only when explicitly chosen.
  const [view, setView] = useState<'tiles' | 'rows'>(() =>
    localStorage.getItem('crlView') === 'tiles' ? 'tiles' : 'rows'
  );
  const changeView = (v: 'tiles' | 'rows') => {
    setView(v);
    localStorage.setItem('crlView', v);
  };

  // The column header sits outside the row scroller (see the markup below) so it can stay
  // transparent like the studies page. Being outside, it cannot follow a horizontal scroll
  // on its own — this slides it by the same amount so every label stays over its column.
  // Written straight to the node rather than through state: it fires on every scroll frame
  // and must not re-render a list this size.
  const listRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);
  const syncHeadScroll = useCallback(() => {
    const head = headRef.current;
    if (head) {
      head.style.transform = `translateX(${-(listRef.current?.scrollLeft || 0)}px)`;
    }
  }, []);
  // A filter or page change can shorten the list and reset its scroll position, so re-sync:
  // the header must not be left offset against rows that are back at the left edge.
  useEffect(() => {
    syncHeadScroll();
  }, [view, canUseLabels, syncHeadScroll]);

  const { data: doctorsList } = useSelector((s: RootState) => s.getHospitalDoctorsListResponse);
  const { result: commonData } = useSelector((s: RootState) => s.getCommonDataSliceResponse);

  useEffect(() => {
    dispatch(getHospitalDoctorsList({}));
  }, [dispatch]);

  const statusOpts: string[] = commonData?.reportStatus?.length
    ? commonData.reportStatus
    : ['Draft', 'Preliminary', 'Finalized'];
  const typeOpts: string[] = commonData?.reportType || [];
  const dropdownItems = actions?.[0]?.dropdownItems || [];

  const set = (patch: any) => onFilterChange({ ...currentFilters, ...patch });
  // Multi-select status: reportStatus is a comma-joined string (backend splits it → $in).
  const statusList = (currentFilters.reportStatus || '')
    .split(',')
    .map((x: string) => x.trim())
    .filter(Boolean);
  const toggleStatus = (s: string) =>
    set({
      reportStatus: (statusList.includes(s)
        ? statusList.filter((x: string) => x !== s)
        : [...statusList, s]
      ).join(','),
    });
  const isFiltering =
    !!searchTerm ||
    !!currentFilters.reportAssignedTo ||
    !!currentFilters.startDate ||
    !!currentFilters.endDate ||
    !!currentFilters.reportStatus ||
    !!currentFilters.reportType ||
    !!currentFilters.labels ||
    !!currentFilters.measurementFilters;

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const cur = pagination.currentPage || 1;
  const pageWindow = useMemo(() => {
    const span = 5;
    let start = Math.max(1, cur - 2);
    const end = Math.min(totalPages, start + span - 1);
    start = Math.max(1, end - span + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [cur, totalPages]);

  const startIdx = pagination.totalRecords === 0 ? 0 : (cur - 1) * pagination.pageSize + 1;
  const endIdx = Math.min(cur * pagination.pageSize, pagination.totalRecords);

  return (
    <div className={rootClass}>
      {/* command bar */}
      <div className="csl-cmd">
        <div className="csl-cmd__title">
          <h1 className="csl-h1">Report List</h1>
          <span className="csl-count">
            <b>{pagination.totalRecords}</b> reports
          </span>
        </div>
        <div className="csl-cmd__tools">
          <label className="csl-search">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search patient name…"
              defaultValue={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </label>
          <div
            className="crl-viewtoggle"
            role="group"
            aria-label="View"
          >
            <button
              className={view === 'rows' ? 'is-active' : ''}
              title="List view"
              onClick={() => changeView('rows')}
            >
              <svg
                viewBox="0 0 24 24"
                width="17"
                height="17"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            </button>
            <button
              className={view === 'tiles' ? 'is-active' : ''}
              title="Grid view"
              onClick={() => changeView('tiles')}
            >
              <svg
                viewBox="0 0 24 24"
                width="17"
                height="17"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect
                  x="3"
                  y="3"
                  width="7"
                  height="7"
                  rx="1.5"
                />
                <rect
                  x="14"
                  y="3"
                  width="7"
                  height="7"
                  rx="1.5"
                />
                <rect
                  x="3"
                  y="14"
                  width="7"
                  height="7"
                  rx="1.5"
                />
                <rect
                  x="14"
                  y="14"
                  width="7"
                  height="7"
                  rx="1.5"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* filter rail */}
      <div className="csl-filters">
        <div className="csl-seg">
          <button
            className={`csl-seg__btn ${statusList.length === 0 ? 'is-active' : ''}`}
            onClick={() => set({ reportStatus: '' })}
          >
            All
          </button>
          {statusOpts.map(s => (
            <button
              key={s}
              className={`csl-seg__btn ${statusClass(s)} ${statusList.includes(s) ? 'is-active' : ''}`}
              onClick={() => toggleStatus(s)}
            >
              {s}
            </button>
          ))}
        </div>
        {/* Labels are the receptionist's primary filter — the whole point is scanning
            "what have I already filed". Kept on the always-visible bar rather than behind
            More filters so it doesn't cost a click on every pass. */}
        {canUseLabels && (
          <ReportLabelFilter
            labels={labels}
            value={currentFilters.labels || ''}
            onChange={labelId => set({ labels: labelId })}
          />
        )}
        {canFilterMeasurements && (
          <MeasurementFilter
            value={currentFilters.measurementFilters || ''}
            onChange={v => set({ measurementFilters: v })}
          />
        )}
        <button
          className={`csl-more ${moreOpen ? 'is-open' : ''}`}
          onClick={() => setMoreOpen(o => !o)}
        >
          <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          More filters
        </button>
        {isFiltering && (
          <button
            className="csl-clear"
            onClick={() => {
              setSearchTerm('');
              onClearFilter();
              setMoreOpen(false);
            }}
          >
            Clear all
          </button>
        )}
      </div>

      {moreOpen && (
        <div className="csl-more-panel">
          <label className="csl-field">
            <span>Assigned to</span>
            <select
              value={currentFilters.reportAssignedTo || ''}
              onChange={e => set({ reportAssignedTo: e.target.value })}
            >
              <option value="">Anyone</option>
              {(Array.isArray(doctorsList) ? doctorsList : []).map((d: any, i: number) => (
                <option
                  key={i}
                  value={d?.name}
                >
                  {d?.name}
                </option>
              ))}
            </select>
          </label>
          <label className="csl-field">
            <span>Report type</span>
            <select
              value={currentFilters.reportType || ''}
              onChange={e => set({ reportType: e.target.value })}
            >
              <option value="">All types</option>
              {typeOpts.map((tp, i) => (
                <option
                  key={i}
                  value={tp}
                >
                  {tp}
                </option>
              ))}
            </select>
          </label>
          <label className="csl-field">
            <span>Start date</span>
            <input
              type="date"
              value={currentFilters.startDate || ''}
              onChange={e => set({ startDate: e.target.value })}
            />
          </label>
          <label className="csl-field">
            <span>End date</span>
            <input
              type="date"
              value={currentFilters.endDate || ''}
              onChange={e => set({ endDate: e.target.value })}
            />
          </label>
        </div>
      )}

      {/* Column header band — OUTSIDE the scrolling list, exactly like the studies
          page. Kept outside so nothing ever passes underneath it: a header pinned
          inside the scroller needs an opaque fill to hide the rows sliding under,
          and that reads as a black bar against the page gradient. Staying outside
          means it can be transparent like Studies. The cost is that it no longer
          follows a horizontal scroll on its own, so syncHeadScroll drives it. */}
      {view === 'rows' && reports.length > 0 && (
        <Box sx={{ overflow: 'hidden', flexShrink: 0 }}>
          <Box
            ref={headRef}
            aria-hidden
            sx={{
              display: 'grid',
              gridTemplateColumns: canUseLabels ? GRID_COLS_LABELS : GRID_COLS,
              minWidth: canUseLabels ? ROW_MIN_W_LABELS : ROW_MIN_W,
              alignItems: 'center',
              gap: canUseLabels ? '12px' : '16px',
              // Matches the studies band exactly (mx 4px, pl 22px, pr 18px, pb 8px).
              mx: '4px',
              pl: '22px',
              pr: '18px',
              pb: '8px',
            }}
          >
            <HeadCell>Name</HeadCell>
            <HeadCell>Assigned</HeadCell>
            <HeadCell>Date</HeadCell>
            <HeadCell>Type</HeadCell>
            <HeadCell>Distribution</HeadCell>
            {canUseLabels && <HeadCell>Labels</HeadCell>}
            <HeadCell>Status</HeadCell>
            <HeadCell align="end">Actions</HeadCell>
          </Box>
        </Box>
      )}

      {/* report tiles — a grid of vertical cards (distinct from the studies row list) */}
      <div
        className="csl-list"
        ref={listRef}
        onScroll={syncHeadScroll}
      >
        {loading && !reports.length && (
          <div className="csl-empty">
            <svg
              className="csl-ekg"
              viewBox="0 0 150 46"
              fill="none"
              aria-hidden="true"
            >
              <polyline
                className="csl-ekg__line"
                points="0,23 40,23 50,23 56,9 64,37 72,23 92,23 100,15 108,31 116,23 150,23"
              />
              <polyline
                className="csl-ekg__pulse"
                points="0,23 40,23 50,23 56,9 64,37 72,23 92,23 100,15 108,31 116,23 150,23"
              />
            </svg>
            Loading reports…
          </div>
        )}
        {!loading && !reports.length && (
          <div className="csl-empty">
            <div className="csl-empty__icon">📄</div>
            <div className="csl-empty__t">No reports found</div>
            <div className="csl-empty__s">
              {isFiltering ? 'Try clearing filters' : 'Finalized & draft reports will appear here'}
            </div>
          </div>
        )}

        {view === 'tiles' && (
          <div className="crl-grid">
            {reports.map((item: any) => {
              const status = item.status || 'Draft';
              return (
                <article
                  key={item._id}
                  className={`crl-tile ${statusClass(status)} ${menuTarget?._id === item._id ? 'is-menu-open' : ''}`}
                  onClick={() => openReport(item)}
                >
                  <div className="crl-tile__top">
                    <span className="csl-avatar">{initialsOf(item.name)}</span>
                    {renderCritical(item)}
                    <span className={`csl-status ${statusClass(status)}`}>{status}</span>
                    <div
                      className="crl-tile__menu"
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        className="csl-icon"
                        title="Actions"
                        aria-haspopup="menu"
                        onClick={e => openMenu(item, e)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="currentColor"
                        >
                          <circle
                            cx="12"
                            cy="5"
                            r="1.6"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="1.6"
                          />
                          <circle
                            cx="12"
                            cy="19"
                            r="1.6"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="crl-tile__name">
                    {item.name || '(Unknown patient)'}
                    {renderBell(item)}
                  </div>
                  <div className="crl-tile__id">
                    {item.patientId ? `MRN ${item.patientId}` : ''}
                  </div>

                  <div className="crl-tile__divider" />

                  <dl className="crl-tile__meta">
                    <div>
                      <dt>Assigned</dt>
                      <dd>{renderAssigned(item)}</dd>
                    </div>
                    <div>
                      <dt>Date</dt>
                      <dd>
                        {reportDate(item) || '—'}
                        {reportTime(item) && ` · ${reportTime(item)}`}
                      </dd>
                    </div>
                    <div>
                      <dt>Type</dt>
                      <dd>
                        <span className="csl-modality">{item.reportName || '—'}</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Distribution</dt>
                      <dd>{renderDist(item)}</dd>
                    </div>
                    {canUseLabels && (
                      <div>
                        <dt>Labels</dt>
                        <dd>{renderLabels(item)}</dd>
                      </div>
                    )}
                  </dl>
                </article>
              );
            })}
          </div>
        )}

        {view === 'rows' && (
          // minWidth:'fit-content' makes this wrapper grow to its widest row instead of
          // stretching to the scroller's width. Without it the rows overflow a
          // container-width box, and .csl-list computes its scrollable extent from that
          // box — so the last columns get clipped rather than reachable by scrolling.
          // flexShrink:0 stops the parent flex column squeezing it back down.
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              minWidth: 'fit-content',
              flexShrink: 0,
            }}
          >
            {reports.map((item: any) => {
              const status = item.status || 'Draft';
              const sColor = statusColor(status);
              const isMenuOpen = menuTarget?._id === item._id;
              return (
                <Card
                  key={item._id}
                  onClick={() => openReport(item)}
                  sx={{
                    position: 'relative',
                    // The action menu of the open row must paint above later rows.
                    zIndex: isMenuOpen ? 60 : 'auto',
                    overflow: 'visible',
                    // A flex column would otherwise shrink a row below its min-width and
                    // the horizontal scroll would never appear.
                    flexShrink: 0,
                    display: 'grid',
                    gridTemplateColumns: canUseLabels ? GRID_COLS_LABELS : GRID_COLS,
                    minWidth: canUseLabels ? ROW_MIN_W_LABELS : ROW_MIN_W,
                    alignItems: 'center',
                    gap: canUseLabels ? '12px' : '16px',
                    p: '14px 18px 14px 22px',
                    cursor: 'pointer',
                    background: 'linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))',
                    border: `1px solid ${T.line}`,
                    borderRadius: '16px',
                    backdropFilter: 'blur(7px)',
                    transition:
                      'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease, background 0.16s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: T.lineStrong,
                      background:
                        'linear-gradient(180deg, rgba(30,50,76,0.78), rgba(18,34,54,0.75))',
                      boxShadow: '0 16px 38px -18px rgba(0,0,0,0.85)',
                    },
                  }}
                >
                  {/* status stripe */}
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 14,
                      bottom: 14,
                      width: 3,
                      borderRadius: '0 3px 3px 0',
                      background: sColor,
                    }}
                  />

                  {/* identity */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', minWidth: 0 }}>
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        fontSize: 14,
                        fontWeight: 700,
                        letterSpacing: '0.03em',
                        color: '#04221d',
                        // Circular in the report ROW view (the old
                        // `.crl-rows .csl-avatar { border-radius: 50% }`). Studies uses a
                        // 12px rounded square; reports has always differed here.
                        borderRadius: '50%',
                        background: isCritical(item)
                          ? 'linear-gradient(135deg,#fb7185,#e11d48)'
                          : `linear-gradient(135deg, ${T.teal}, ${T.sky})`,
                        boxShadow: isCritical(item)
                          ? '0 4px 14px -4px rgba(225,29,72,0.6)'
                          : '0 4px 14px -4px rgba(45,212,191,0.5)',
                      }}
                    >
                      {initialsOf(item.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {/* The name text gets its OWN element. textOverflow:ellipsis has no
                          effect on a flex container — the text would become an anonymous
                          flex item and get hard-clipped mid-letter with no "…", which is
                          exactly how "PARADISE, CHRISTO" was rendering. */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '7px',
                          fontSize: 15,
                          fontWeight: 700,
                          color: '#fff',
                          minWidth: 0,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.name || '(Unknown patient)'}
                        </Box>
                        <Box
                          component="span"
                          sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
                        >
                          {renderCritical(item)}
                          {renderAF(item)}
                          {renderBell(item)}
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: T.ink3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.patientId ? `MRN ${item.patientId}` : item.reportName || ''}
                      </Typography>
                    </Box>
                  </Box>

                  {/* assigned — renderAssigned carries the Update button */}
                  <Box sx={{ ...colSx, minWidth: 0 }}>
                    <Box
                      sx={{
                        ...vSx,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: 0,
                      }}
                    >
                      {renderAssigned(item)}
                    </Box>
                  </Box>

                  <Box sx={colSx}>
                    <Typography sx={vSx}>{reportDate(item) || '—'}</Typography>
                    {reportTime(item) && (
                      <Typography sx={{ fontSize: 12, color: T.ink3 }}>{reportTime(item)}</Typography>
                    )}
                  </Box>

                  <Box sx={colSx}>
                    <Typography
                      sx={{
                        alignSelf: 'flex-start',
                        fontSize: 12,
                        fontWeight: 700,
                        color: T.violet,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.reportName || '—'}
                    </Typography>
                  </Box>

                  <Box sx={colSx}>{renderDist(item)}</Box>

                  {canUseLabels && <Box sx={colSx}>{renderLabels(item)}</Box>}

                  <Box sx={{ ...colSx, alignItems: 'flex-start' }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        padding: '4px 14px',
                        borderRadius: '999px',
                        whiteSpace: 'nowrap',
                        color: sColor,
                        background: `${sColor}1f`,
                        border: `1px solid ${sColor}66`,
                      }}
                    >
                      {status}
                    </Box>
                  </Box>

                  <Box
                    onClick={e => e.stopPropagation()}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                  >
                    <button
                      className="csl-icon"
                      title="Actions"
                      aria-haspopup="menu"
                      onClick={e => openMenu(item, e)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="currentColor"
                      >
                        <circle
                          cx="12"
                          cy="5"
                          r="1.6"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="1.6"
                        />
                        <circle
                          cx="12"
                          cy="19"
                          r="1.6"
                        />
                      </svg>
                    </button>
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}
      </div>

      {/* pager */}
      {reports.length > 0 && (
        <div className="csl-pager">
          <div className="csl-pager__per">
            <span>
              {startIdx}–{endIdx} of {pagination.totalRecords}
            </span>
            <select
              value={pagination.pageSize}
              onChange={e => onItemsPerPageChange(Number(e.target.value))}
            >
              {RPP.map(n => (
                <option
                  key={n}
                  value={n}
                >
                  {n}/page
                </option>
              ))}
            </select>
          </div>
          <div className="csl-pager__nav">
            <button
              disabled={cur <= 1}
              onClick={() => onPageChange(1)}
            >
              « First
            </button>
            <button
              disabled={cur <= 1}
              onClick={() => onPageChange(cur - 1)}
            >
              ‹ Prev
            </button>
            {pageWindow.map(p => (
              <button
                key={p}
                className={`crl-page ${p === cur ? 'is-active' : ''}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            ))}
            <button
              disabled={cur >= totalPages}
              onClick={() => onPageChange(cur + 1)}
            >
              Next ›
            </button>
            <button
              disabled={cur >= totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              Last »
            </button>
          </div>
        </div>
      )}

      {/* Row-actions dropdown — MUI Menu portals to <body>, so it can never be clipped
          by the .csl-list overflow scroller, and it repositions/closes itself. */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        onClick={e => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 190,
              bgcolor: '#0e1c2e',
              color: '#e9f0f8',
              border: '1px solid #243652',
              borderRadius: 2,
              boxShadow: '0 18px 44px -16px rgba(0,0,0,0.85)',
              '& .MuiMenuItem-root': {
                fontSize: 14,
                py: 1,
                px: 1.75,
                '&:hover': { bgcolor: 'rgba(45,212,191,0.12)', color: '#2dd4bf' },
              },
            },
          },
        }}
      >
        {menuTarget &&
        dropdownItems.filter(d => (d.isVisible ? d.isVisible(menuTarget) : true)).length ? (
          dropdownItems
            .filter(d => (d.isVisible ? d.isVisible(menuTarget) : true))
            .map(d => (
              <MenuItem
                key={d.id}
                onClick={() => {
                  d.onClick(menuTarget);
                  closeMenu();
                }}
              >
                {d.label}
              </MenuItem>
            ))
        ) : (
          <MenuItem
            disabled
            sx={{ color: '#6a7e9c' }}
          >
            No actions
          </MenuItem>
        )}
      </Menu>

      {/* Portal to body so the fixed/centered modal escapes the .csl flex column */}
      {createPortal(
        <AssignDoctorModal
          isAssignDoctorModalOpen={assignModalOpen}
          reportId={assignReportId}
          AssignReportStatus={assignStatus}
          onClose={() => setAssignModalOpen(false)}
          setOnAssigningReport={(v: boolean) => {
            if (v) {
              onAssigned?.();
              setAssignModalOpen(false);
            }
          }}
        />,
        document.body
      )}
    </div>
  );
};

export default CardiacReportList;
